import React, { useState } from 'react';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { AlertCircle, ArrowRight, ArrowUp, ArrowDown } from 'lucide-react';
import type { GaitSession, StrideData } from '../AdvancedGaitAnalysis';

interface GaitAsymmetryProps {
  session: GaitSession;
  selectedStride: StrideData | undefined;
  compareWithBaseline: boolean;
  onStrideChange: (strideId: string) => void;
}

// Mock baseline data
const baselineStride: StrideData = {
  id: 'baseline-stride',
  timestamp: Date.now() - 30 * 86400000,
  leftStep: {
    id: 'baseline-stride-left',
    timestamp: Date.now() - 30 * 86400000,
    footSide: 'left',
    pressurePoints: [],
    centerOfPressure: { x: 25, y: 50 },
    peakPressure: 82,
    contactArea: 145,
    duration: 580,
    stepLength: 58,
    propulsionForce: 440
  },
  rightStep: {
    id: 'baseline-stride-right',
    timestamp: Date.now() - 30 * 86400000 + 580,
    footSide: 'right',
    pressurePoints: [],
    centerOfPressure: { x: 65, y: 50 },
    peakPressure: 80,
    contactArea: 142,
    duration: 575,
    stepLength: 57,
    propulsionForce: 435
  },
  strideLength: 115,
  strideTime: 1155,
  symmetryIndex: 95,
  cadence: 52,
  velocity: 100,
  baseOfSupport: 12
};

const GaitAsymmetry: React.FC<GaitAsymmetryProps> = ({
  session,
  selectedStride,
  compareWithBaseline,
  onStrideChange
}) => {
  const [metricView, setMetricView] = useState<'individual' | 'trend'>('individual');
  const [highlightCategory, setHighlightCategory] = useState<string | null>(null);

  if (!selectedStride) {
    return <div className="p-6 text-center text-gray-500">No stride data selected</div>;
  }

  // Compute asymmetry data from the current stride
  const currentAsymmetryData = [
    {
      metric: 'Step Length',
      left: selectedStride.leftStep.stepLength,
      right: selectedStride.rightStep.stepLength,
      asymmetry: calculateAsymmetry(selectedStride.leftStep.stepLength, selectedStride.rightStep.stepLength),
      baselineAsymmetry: calculateAsymmetry(baselineStride.leftStep.stepLength, baselineStride.rightStep.stepLength),
      unit: 'cm',
      category: 'spatial'
    },
    {
      metric: 'Step Time',
      left: selectedStride.leftStep.duration,
      right: selectedStride.rightStep.duration,
      asymmetry: calculateAsymmetry(selectedStride.leftStep.duration, selectedStride.rightStep.duration),
      baselineAsymmetry: calculateAsymmetry(baselineStride.leftStep.duration, baselineStride.rightStep.duration),
      unit: 'ms',
      category: 'temporal'
    },
    {
      metric: 'Peak Pressure',
      left: selectedStride.leftStep.peakPressure,
      right: selectedStride.rightStep.peakPressure,
      asymmetry: calculateAsymmetry(selectedStride.leftStep.peakPressure, selectedStride.rightStep.peakPressure),
      baselineAsymmetry: calculateAsymmetry(baselineStride.leftStep.peakPressure, baselineStride.rightStep.peakPressure),
      unit: 'N/cm²',
      category: 'pressure'
    },
    {
      metric: 'Contact Area',
      left: selectedStride.leftStep.contactArea,
      right: selectedStride.rightStep.contactArea,
      asymmetry: calculateAsymmetry(selectedStride.leftStep.contactArea, selectedStride.rightStep.contactArea),
      baselineAsymmetry: calculateAsymmetry(baselineStride.leftStep.contactArea, baselineStride.rightStep.contactArea),
      unit: 'cm²',
      category: 'pressure'
    },
    {
      metric: 'Propulsion Force',
      left: selectedStride.leftStep.propulsionForce,
      right: selectedStride.rightStep.propulsionForce,
      asymmetry: calculateAsymmetry(selectedStride.leftStep.propulsionForce, selectedStride.rightStep.propulsionForce),
      baselineAsymmetry: calculateAsymmetry(baselineStride.leftStep.propulsionForce, baselineStride.rightStep.propulsionForce),
      unit: 'N',
      category: 'kinetic'
    }
  ];

  // Group asymmetry metrics by category for radar chart
  const radarData = [
    { metric: 'Step Length', value: currentAsymmetryData[0].asymmetry, baseline: currentAsymmetryData[0].baselineAsymmetry, fullMark: 30 },
    { metric: 'Step Time', value: currentAsymmetryData[1].asymmetry, baseline: currentAsymmetryData[1].baselineAsymmetry, fullMark: 30 },
    { metric: 'Peak Pressure', value: currentAsymmetryData[2].asymmetry, baseline: currentAsymmetryData[2].baselineAsymmetry, fullMark: 30 },
    { metric: 'Contact Area', value: currentAsymmetryData[3].asymmetry, baseline: currentAsymmetryData[3].baselineAsymmetry, fullMark: 30 },
    { metric: 'Propulsion', value: currentAsymmetryData[4].asymmetry, baseline: currentAsymmetryData[4].baselineAsymmetry, fullMark: 30 }
  ];

  // Create trend data across session strides
  const trendData = session.strides.map(stride => {
    return {
      strideId: stride.id,
      strideNumber: session.strides.findIndex(s => s.id === stride.id) + 1,
      stepLengthAsymmetry: calculateAsymmetry(stride.leftStep.stepLength, stride.rightStep.stepLength),
      stepTimeAsymmetry: calculateAsymmetry(stride.leftStep.duration, stride.rightStep.duration),
      peakPressureAsymmetry: calculateAsymmetry(stride.leftStep.peakPressure, stride.rightStep.peakPressure),
      contactAreaAsymmetry: calculateAsymmetry(stride.leftStep.contactArea, stride.rightStep.contactArea),
      propulsionAsymmetry: calculateAsymmetry(stride.leftStep.propulsionForce, stride.rightStep.propulsionForce),
      overallAsymmetry: 100 - stride.symmetryIndex, // Invert symmetry index to get asymmetry
      isSelected: stride.id === selectedStride.id
    };
  });

  // Calculate overall asymmetry score
  const asymmetryScore = 100 - selectedStride.symmetryIndex;
  const baselineAsymmetryScore = 100 - baselineStride.symmetryIndex;
  const asymmetryScoreChange = asymmetryScore - baselineAsymmetryScore;

  const isSignificantAsymmetry = asymmetryScore > 15;
  const isWorseningAsymmetry = asymmetryScoreChange > 5;

  // Helper function to calculate asymmetry percentage
  function calculateAsymmetry(left: number, right: number): number {
    const avg = (left + right) / 2;
    if (avg === 0) return 0;
    return Math.abs(left - right) / avg * 100;
  }

  // Get color for asymmetry value
  function getAsymmetryColor(value: number): string {
    if (value < 5) return 'text-green-600';
    if (value < 10) return 'text-yellow-600';
    return 'text-red-600';
  }

  const handleClickStride = (strideId: string) => {
    onStrideChange(strideId);
  };

  const renderLeftRightComparison = () => (
    <div className="bg-white rounded-lg shadow p-4 mb-6">
      <h3 className="font-medium border-b pb-2 mb-4">Left vs. Right Comparison</h3>
      
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={currentAsymmetryData}
          layout="vertical"
          margin={{ top: 20, right: 30, left: 80, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis dataKey="metric" type="category" />
          <Tooltip 
            formatter={(value, name) => {
              const unit = currentAsymmetryData.find(d => 
                name === 'left' ? d.left === value : d.right === value
              )?.unit || '';
              return [`${value} ${unit}`, name === 'left' ? 'Left Foot' : 'Right Foot'];
            }}
          />
          <Legend />
          <Bar 
            dataKey="left" 
            name="Left Foot" 
            fill="#3b82f6" 
            minPointSize={2} 
            barSize={20}
            onMouseEnter={() => setHighlightCategory(null)}
          />
          <Bar 
            dataKey="right" 
            name="Right Foot" 
            fill="#f97316" 
            minPointSize={2}
            barSize={20}
            onMouseEnter={() => setHighlightCategory(null)}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  const renderAsymmetryMetrics = () => (
    <div className="bg-white rounded-lg shadow p-4 mb-6">
      <h3 className="font-medium border-b pb-2 mb-4">Asymmetry Metrics</h3>
      
      <div className="mb-4">
        <div className="flex flex-wrap">
          {['spatial', 'temporal', 'pressure', 'kinetic'].map(category => (
            <button
              key={category}
              className={`mr-2 mb-2 px-3 py-1 text-xs rounded-full ${
                highlightCategory === category
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => setHighlightCategory(
                highlightCategory === category ? null : category
              )}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {currentAsymmetryData.map((item, index) => (
          <div 
            key={index}
            className={`border rounded-lg p-3 transition-colors ${
              highlightCategory === null || highlightCategory === item.category
                ? 'opacity-100'
                : 'opacity-50'
            } ${
              item.asymmetry > 15
                ? 'bg-red-50 border-red-200'
                : item.asymmetry > 10
                ? 'bg-yellow-50 border-yellow-200'
                : 'bg-green-50 border-green-200'
            }`}
          >
            <div className="flex justify-between items-center mb-2">
              <span className="font-medium">{item.metric}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                item.category === 'spatial' ? 'bg-blue-100 text-blue-800' :
                item.category === 'temporal' ? 'bg-purple-100 text-purple-800' :
                item.category === 'pressure' ? 'bg-green-100 text-green-800' :
                'bg-orange-100 text-orange-800'
              }`}>
                {item.category}
              </span>
            </div>
            
            <div className="text-2xl font-bold mb-1 flex items-center">
              {item.asymmetry.toFixed(1)}%
              {compareWithBaseline && (
                <span className="ml-2 text-sm font-normal flex items-center">
                  {item.asymmetry > item.baselineAsymmetry ? (
                    <>
                      <ArrowUp className="text-red-500" size={16} />
                      <span className="text-red-500">
                        {(item.asymmetry - item.baselineAsymmetry).toFixed(1)}%
                      </span>
                    </>
                  ) : item.asymmetry < item.baselineAsymmetry ? (
                    <>
                      <ArrowDown className="text-green-500" size={16} />
                      <span className="text-green-500">
                        {(item.baselineAsymmetry - item.asymmetry).toFixed(1)}%
                      </span>
                    </>
                  ) : (
                    <ArrowRight className="text-gray-500" size={16} />
                  )}
                </span>
              )}
            </div>
            
            <div className="text-sm text-gray-600 mb-2">
              Left: {item.left.toFixed(1)} {item.unit} | Right: {item.right.toFixed(1)} {item.unit}
            </div>
            
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${
                  item.asymmetry > 15
                    ? 'bg-red-500'
                    : item.asymmetry > 10
                    ? 'bg-yellow-500'
                    : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(item.asymmetry * 2, 100)}%` }}
              />
            </div>
            
            {compareWithBaseline && (
              <div className="mt-1 text-xs text-gray-500 flex justify-between">
                <span>Baseline: {item.baselineAsymmetry.toFixed(1)}%</span>
                <span className={getAsymmetryColor(item.asymmetry - item.baselineAsymmetry)}>
                  {item.asymmetry > item.baselineAsymmetry 
                    ? `+${(item.asymmetry - item.baselineAsymmetry).toFixed(1)}%` 
                    : item.asymmetry < item.baselineAsymmetry
                    ? `-${(item.baselineAsymmetry - item.asymmetry).toFixed(1)}%`
                    : 'No change'}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderOverallAsymmetry = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="font-medium border-b pb-2 mb-4">Overall Asymmetry</h3>
        
        <div className="flex items-center justify-center flex-col h-40">
          <div className={`text-5xl font-bold mb-2 ${
            asymmetryScore > 15
              ? 'text-red-600'
              : asymmetryScore > 10
              ? 'text-yellow-600'
              : 'text-green-600'
          }`}>
            {asymmetryScore.toFixed(1)}%
          </div>
          
          {compareWithBaseline && (
            <div className="flex items-center space-x-1 text-sm">
              <span className="text-gray-600">From baseline:</span>
              <span className={
                asymmetryScoreChange > 0 
                  ? 'text-red-600' 
                  : asymmetryScoreChange < 0
                  ? 'text-green-600'
                  : 'text-gray-600'
              }>
                {asymmetryScoreChange > 0 
                  ? `+${asymmetryScoreChange.toFixed(1)}%` 
                  : asymmetryScoreChange < 0
                  ? `${asymmetryScoreChange.toFixed(1)}%`
                  : 'No change'}
              </span>
            </div>
          )}
          
          {isSignificantAsymmetry && isWorseningAsymmetry && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-2 flex items-center text-sm">
              <AlertCircle size={16} className="text-red-500 mr-2 flex-shrink-0" />
              <span className="text-red-800">
                Significant asymmetry increase detected
              </span>
            </div>
          )}
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow p-4 lg:col-span-2">
        <h3 className="font-medium border-b pb-2 mb-4">Asymmetry Pattern</h3>
        
        <div className="flex justify-center">
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart outerRadius={90} data={radarData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="metric" />
              <PolarRadiusAxis angle={90} domain={[0, 30]} />
              <Radar
                name="Current"
                dataKey="value"
                stroke="#3b82f6"
                fill="#3b82f6"
                fillOpacity={0.5}
              />
              {compareWithBaseline && (
                <Radar
                  name="Baseline"
                  dataKey="baseline"
                  stroke="#9ca3af"
                  fill="#9ca3af"
                  fillOpacity={0.3}
                />
              )}
              <Legend />
              <Tooltip formatter={(value) => `${Number(value).toFixed(1)}%`} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );

  const renderAsymmetryTrend = () => (
    <div className="bg-white rounded-lg shadow p-4 mb-6">
      <h3 className="font-medium border-b pb-2 mb-4">Asymmetry Trend Across Session</h3>
      
      <div className="mb-4">
        <div className="flex space-x-2">
          <button
            onClick={() => setMetricView('individual')}
            className={`px-3 py-1 text-sm rounded ${
              metricView === 'individual' 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            By Metric
          </button>
          <button
            onClick={() => setMetricView('trend')}
            className={`px-3 py-1 text-sm rounded ${
              metricView === 'trend' 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Overall Trend
          </button>
        </div>
      </div>
      
      <ResponsiveContainer width="100%" height={300}>
        {metricView === 'individual' ? (
          <LineChart data={trendData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="strideNumber" />
            <YAxis domain={[0, 30]} />
            <Tooltip formatter={(value, name) => [`${Number(value).toFixed(1)}%`, name]} />
            <Legend />
            <Line
              type="monotone"
              dataKey="stepLengthAsymmetry"
              name="Step Length"
              stroke="#3b82f6"
              activeDot={{ onClick: (data: any) => handleClickStride(data.payload.strideId) }}
            />
            <Line
              type="monotone"
              dataKey="stepTimeAsymmetry"
              name="Step Time"
              stroke="#8b5cf6"
              activeDot={{ onClick: (data: any) => handleClickStride(data.payload.strideId) }}
            />
            <Line
              type="monotone"
              dataKey="peakPressureAsymmetry"
              name="Peak Pressure"
              stroke="#10b981"
              activeDot={{ onClick: (data: any) => handleClickStride(data.payload.strideId) }}
            />
            <Line
              type="monotone"
              dataKey="propulsionAsymmetry"
              name="Propulsion"
              stroke="#f97316"
              activeDot={{ onClick: (data: any) => handleClickStride(data.payload.strideId) }}
            />
          </LineChart>
        ) : (
          <LineChart data={trendData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="strideNumber" />
            <YAxis domain={[0, 30]} />
            <Tooltip formatter={(value) => `${Number(value).toFixed(1)}%`} />
            <Legend />
            <Line
              type="monotone"
              dataKey="overallAsymmetry"
              name="Overall Asymmetry"
              stroke="#ef4444"
              strokeWidth={2}
              activeDot={{ onClick: (data: any) => handleClickStride(data.payload.strideId) }}
            />
            {compareWithBaseline && (
              <Line
                type="monotone"
                dataKey={() => baselineAsymmetryScore}
                name="Baseline Asymmetry"
                stroke="#9ca3af"
                strokeDasharray="4 4"
              />
            )}
          </LineChart>
        )}
      </ResponsiveContainer>
      
      <div className="text-sm text-gray-500 mt-2 text-center">
        Click on any data point to select that stride
      </div>
    </div>
  );

  return (
    <div className="p-6">
      {renderOverallAsymmetry()}
      {renderAsymmetryMetrics()}
      {renderLeftRightComparison()}
      {renderAsymmetryTrend()}
    </div>
  );
};

export default GaitAsymmetry; 